import React from "react";
import BooksNavbar from "../components/BooksNavbar";

const Dementia = () => {
  return (
    <div>
      <section
        className="relative min-h-screen bg-cover bg-center overflow-hidden"
        style={{ backgroundImage: "url('/water-colour-clouds.png')" }}
      >
        <BooksNavbar textColor="black" />

        <div className="relative z-10 min-h-screen pt-28 pb-16">
          {/* Page Title */}
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-16">
            <h2
              className="text-lg md:text-xl text-[#5f6f4f] mb-4"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              LEARN ABOUT THE SCIENCE
            </h2>

            <div className="flex items-center w-full max-w-sm mb-6 mx-auto">
              <div className="flex-1 h-[1px] bg-[#8c8b5f]"></div>
              <span className="px-4 text-[#8c8b5f] text-lg">❖</span>
              <div className="flex-1 h-[1px] bg-[#8c8b5f]"></div>
            </div>

            <h1
              className="text-4xl md:text65xl text-black leading-tight mb-4"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Dementia
            </h1>

            <p
              className="text-xl md:text-2xl italic text-[#5f6f4f] leading-snug mb-6"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              What If the First Symptom Isn't Memory Loss?
            </p>

            <div className="flex items-center w-full max-w-sm mx-auto">
              <div className="flex-1 h-[1px] bg-[#8c8b5f]"></div>
              <span className="px-4 text-[#8c8b5f]">❖</span>
              <div className="flex-1 h-[1px] bg-[#8c8b5f]"></div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Left Column */}
              <div className="max-w-2xl">
                <p
                  className="text-base text-black leading-relaxed mb-6"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  When most people think about dementia, they think about
                  memory. They imagine someone forgetting names, misplacing
                  keys, or struggling to recall important events. And while
                  memory loss is certainly one of the hallmarks of Alzheimer's
                  disease and other dementias, neuroscience is beginning to tell
                  a more complicated — and more hopeful — story. What if the
                  earliest warning signs appear years before any obvious memory
                  problems begin? What if they are hiding in plain sight?
                </p>

                <p
                  className="text-base text-black leading-relaxed py-2 mb-2"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Today, more than 55 million people worldwide live with
                  dementia, and that number is expected to more than double over
                  the coming decades. Yet by the time most people receive a
                  diagnosis, the disease has often been silently developing in
                  the brain for years. That's why early detection has become one
                  of the most important challenges in modern neuroscience.
                </p>
              </div>

              {/* right image */}
              <div className="flex justify-center lg:justify-end">
                <img
                  src="/dementia.png"
                  alt="Dementia"
                  className="w-full max-w-[600px]"
                />
              </div>
            </div>

            {/* Full Width Text */}
            <div className="mt-2">
              <p
                className="text-base text-black leading-relaxed mb-6"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Contrary to popular belief, an early diagnosis is not simply
                about predicting the future. It creates opportunities. Blood
                pressure can be treated. Diabetes can be managed. Sleep can be
                improved. Exercise can be increased. New drugs are emerging that
                may slow disease progression, but many appear to work best when
                given early, before extensive damage has occurred.
              </p>
              <p
                className="text-base text-black leading-relaxed mb-6"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                And here's where things get really interesting. Some of the
                earliest changes may not involve memory at all. Increasing
                evidence suggests that subtle failures of executive function —
                including the ability to ignore distractions, suppress impulsive
                responses, and stay focused on a goal — may provide some of the
                earliest clues that something is changing inside the brain.
              </p>
              <p
                className="text-base text-black leading-relaxed mb-6"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                A few years ago, my team used machine learning to analyse the
                performance of more than 22,000 healthy adults and almost 4,000
                patients attending memory clinics. Remarkably, we found that a
                small number of executive function tests could identify patients
                with cognitive impairment more than 80 percent of the time. One
                of the strongest predictors wasn't a memory test at all — it was
                a test of inhibitory control.
              </p>

              <p
                className="text-base text-black leading-relaxed"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                In <em>Think Before You Think</em>, you'll discover why the
                frontal lobes may hold some of the earliest clues to cognitive
                decline in dementia, how scientists are racing to develop
                inexpensive digital tools that can identify risk years before
                traditional diagnosis, and why distinguishing normal aging from
                the earliest stages of disease may become one of the most
                important healthcare challenges of the twenty-first century.
                Because when it comes to dementia, the most important question
                may not be whether we can cure it. It may be whether we can spot
                it before it takes hold.
              </p>

              {/* Creyos Test Button */}
              <div className="mt-12 text-center">
                <a
                  href="https://habstudy.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#D4AF37] text-black px-4 py-2 font-semibold"
                >
                  IF YOU'D LIKE TO TRY SOME OF THE TESTS BEHIND THE SCIENCE, CLICK HERE
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dementia;
